import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAutoFocus } from "../../hooks/useAutoFocus";
import { Accordion, AccordionDetails, AccordionSummary, Button, FormLabel, Grid, Typography, makeStyles } from "@material-ui/core";
import { AriaErrorScope, TextField, identityRecordOfFieldEditorOptions } from "@ploy-ui/form-fields";
import { isArray } from "lodash";
import { defineMessages, useIntl } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import { Formik, Field as FormikField } from "formik";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { CustomerRole, CustomerType, ErrorDisplay } from "@ploy-lib/types";
import { CalcRuleFormikField } from "../../field/CalcRuleFormikField";
import { createValidationHelpers } from "@ploy-lib/validation-helpers";
const useStyles = makeStyles((theme) => ({
    root: {},
    ownerDetails: {},
    listItem: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2, "!important")
    },
    expanded: {
        backgroundColor: theme.palette.primary.light,
        border: `1px solid ${theme.palette.secondary.light}`
    },
    childNumber: {
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(2)
    },
    header: {
        fontWeight: 700,
        paddingBottom: theme.spacing(4),
        padding: theme.spacing(0.5)
    },
    lenderLabel: {
        overflowWrap: "anywhere",
        fontWeight: "bold"
    },
    boldText: {
        fontWeight: "bold",
        fontSize: 16
    },
    addButton: {
        borderRadius: 4,
        justifyContent: "start",
        padding: "12px 24px",
        border: `1px solid ${theme.palette.common.black}`,
        fontSize: 16,
        background: "#fff",
        marginBottom: theme.spacing(2)
    },
    addButtonPlusIcon: {
        marginRight: 8
    },
    expandMoreIcon: {
        color: theme.palette.primary.main
    },
    spacingAboveField: {
        paddingTop: 12
    }
}), { name: "DployHouseOwnersFromApplicationCustomerField" });
const messages = defineMessages({
    houseOwnerTitle: {
        id: "template-form.field.houseOwner.houseOwnerTitle",
        defaultMessage: "House owner"
    },
    name: {
        id: "template-form.field.houseOwner.name",
        defaultMessage: "Name"
    },
    ssn: {
        id: "template-form.field.houseOwner.ssn",
        defaultMessage: "Social security number"
    },
    mustBeValidSsn: {
        id: "template-form.field.houseOwner.mustBeValidSsn",
        defaultMessage: "SSN must be valid"
    },
    email: {
        id: "template-form.field.houseOwner.email",
        defaultMessage: "E-mail address"
    },
    mustBeValidEmail: {
        id: "template-form.field.houseOwner.mustBeValidEmail",
        defaultMessage: "E-mail must be valid"
    },
    phone: {
        id: "template-form.field.houseOwner.phone",
        defaultMessage: "Phone number"
    },
    mustBeValidPhone: {
        id: "template-form.field.houseOwner.mustBeValidPhoneMobile",
        defaultMessage: "Phone number must be valid"
    },
    save: {
        id: "template-form.field.houseOwner.save",
        defaultMessage: "Save"
    },
    update: {
        id: "template-form.field.houseOwner.update",
        defaultMessage: "Add"
    },
    delete: {
        id: "template-form.field.houseOwner.delete",
        defaultMessage: "Delete"
    },
    addMore: {
        id: "template-form.field.houseOwner.addMore",
        defaultMessage: "Add more"
    },
    mustBeANumber: {
        id: "template-form.field.houseOwner.mustBeANumber",
        defaultMessage: "Must be a number"
    },
    required: {
        id: "template-form.field.houseOwner.required",
        defaultMessage: "Required"
    },
    ssnAlreadyUsed: {
        id: "template-form.field.houseOwner.ssnAlreadyUsed",
        defaultMessage: "SSN is already in use as a house owner"
    },
    noCustomerFound: {
        id: "template-form.field.houseOwner.noCustomerFound",
        defaultMessage: "No customer found with this SSN"
    },
    canNotUseMainCustomer: {
        id: "template-form.field.houseOwner.canNotUseMainCustomer",
        defaultMessage: "You can not use main customer as extra house owner"
    },
    ownerPosition: {
        id: "template-form.field.houseOwner.ownerPosition",
        defaultMessage: "Owner {current} of {total}"
    }
});
const fieldsToTouch = [
    "customerNumber",
    "email",
    "phoneMobile"
];
const fieldsToTouchDictionary = Object.fromEntries(fieldsToTouch.map(key => [key, true]));
function makeHouseOwnerSchema(intl, validationHelpers) {
    return Yup.object({
        customerNumber: Yup.string()
            .required(intl.formatMessage(messages.mustBeValidSsn))
            .test("is-valid-ssn", intl.formatMessage(messages.mustBeValidSsn), validationHelpers.validSsn),
        email: Yup.string()
            .required(intl.formatMessage(messages.mustBeValidEmail))
            .test("is-valid-email", intl.formatMessage(messages.mustBeValidEmail), validationHelpers.emailValidation),
        phoneMobile: Yup.string()
            .required(intl.formatMessage(messages.mustBeValidPhone))
            .test("is-valid-phone-mobile", intl.formatMessage(messages.mustBeValidPhone), validationHelpers.phoneValidation)
    });
}
const doApplicationCustomerSearch = (parameters) => fetch("/api/application-customers/search", {
    method: "POST",
    body: new Blob([JSON.stringify(parameters)], {
        type: "application/json"
    })
}).then(r => {
    if (!r.ok) {
        if (r.status === 409)
            return Promise.reject(CustomerMutationErrors.CannotUseMainCustomer);
        return Promise.reject(CustomerMutationErrors.NoCustomerFound);
    }
    return r.json();
});
const doApplicationCustomerRemove = (parameters) => fetch(`/api/application-customers/clear-application-customer`, {
    method: "POST",
    body: new Blob([JSON.stringify(parameters)], {
        type: "application/json"
    })
}).then(r => {
    if (!r.ok) {
        return null;
    }
    return r.json();
});
const CustomerMutationErrors = {
    NoCustomerFound: "noCustomerFound",
    SsnAlreadyUsed: "ssnAlreadyUsed",
    CannotUseMainCustomer: "cannotUseMainCustomer"
};
function exceptionIsCustomerMutationError(e) {
    return (typeof e === "string" &&
        Object.values(CustomerMutationErrors).includes(e));
}
const HouseOwnerFromApplicationCustomerElement = (props) => {
    const isMounted = useRef(true);
    useEffect(() => () => {
        isMounted.current = false;
    }, []);
    const classes = useStyles();
    const intl = useIntl();
    const validationHelpers = useMemo(() => createValidationHelpers(), []);
    const ownerSchema = useMemo(() => makeHouseOwnerSchema(intl, validationHelpers), [intl, validationHelpers]);
    const [expanded, setExpanded] = useState(!ownerSchema.isValidSync(props.data));
    const { ref: accordionRef } = useAutoFocus(!!props.autoFocus, classes.ownerDetails);
    const { _isNewEntry } = props.data;
    const isExpanded = _isNewEntry || expanded;
    const baseId = `house-owner-${getCustomerId(props.data)}`;
    const ownerName = props.data.customerName?.trim() ||
        intl.formatMessage(messages.houseOwnerTitle);
    const summaryAriaLabel = props.total && props.total > 1 && props.position
        ? `${intl.formatMessage(messages.ownerPosition, {
            current: props.position,
            total: props.total
        })}, ${ownerName}`
        : ownerName;
    const [isRemoving, setIsRemoving] = useState(false);
    const handleOnRemove = useCallback(async () => {
        if (props.onDelete) {
            setIsRemoving(true);
            // No need to setIsRemoving(false)
            await props.onDelete(props.data);
        }
    }, [props]);
    return (_jsx(Formik, { initialValues: props.data, enableReinitialize: true, validationSchema: ownerSchema, onSubmit: async (ownerToUpdate, helpers) => {
            if (props.onUpdate == null) {
                return;
            }
            try {
                const result = await props.onUpdate?.(ownerToUpdate);
                isMounted.current && setExpanded(false);
                return result;
            }
            catch (e) {
                if (exceptionIsCustomerMutationError(e)) {
                    if (e === CustomerMutationErrors.NoCustomerFound) {
                        // Rejection value
                        helpers.setErrors({
                            customerNumber: intl.formatMessage(messages.noCustomerFound)
                        });
                    }
                    else if (e === CustomerMutationErrors.SsnAlreadyUsed) {
                        helpers.setErrors({
                            customerNumber: intl.formatMessage(messages.ssnAlreadyUsed)
                        });
                    }
                    else if (e === CustomerMutationErrors.CannotUseMainCustomer) {
                        helpers.setErrors({
                            customerNumber: intl.formatMessage(messages.canNotUseMainCustomer)
                        });
                    }
                }
                return;
            }
        }, children: formikProps => (_jsxs(Accordion, { ref: accordionRef, expanded: isExpanded, onChange: async (_, updatedExpanded) => {
                const errorMessages = await formikProps.validateForm();
                // validateForm() doesn't set formikProps.isValid => false, or formikProps.isDirty => true..
                // Must instead take a look at validationresult to prevent closing of Accordion
                if (Object.keys(errorMessages).length > 0 || !formikProps.isValid) {
                    // Prevent closing if there are errors
                    formikProps.setTouched(fieldsToTouchDictionary, true);
                    return;
                }
                if (!formikProps.dirty)
                    setExpanded(updatedExpanded);
            }, className: clsx(classes.listItem, {
                [classes.expanded]: isExpanded
            }), children: [_jsx(AccordionSummary, { "aria-label": summaryAriaLabel, expandIcon: _jsx(ExpandMoreIcon, { className: classes.expandMoreIcon }), children: isExpanded ? (_jsx(Typography, { className: classes.lenderLabel, children: intl.formatMessage(messages.houseOwnerTitle) })) : (_jsx(Typography, { className: classes.boldText, children: props.data.customerName?.trim() ||
                            intl.formatMessage(messages.houseOwnerTitle) })) }), _jsx(AccordionDetails, { className: classes.ownerDetails, tabIndex: -1, "aria-label": summaryAriaLabel, children: _jsxs(Grid, { container: true, spacing: 4, direction: "column", children: [_jsx(Grid, { item: true, children: _jsx(FormikField, { name: "customerNumber", id: `${baseId}-customerNumber`, component: TextField, label: intl.formatMessage(messages.ssn), errorDisplay: ErrorDisplay.Touched, fullWidth: true }) }), _jsx(Grid, { item: true, children: _jsx(FormikField, { name: "phoneMobile", id: `${baseId}-phoneMobile`, component: TextField, label: intl.formatMessage(messages.phone), errorDisplay: ErrorDisplay.Touched, fullWidth: true }) }), _jsx(Grid, { item: true, children: _jsx(FormikField, { name: "email", id: `${baseId}-email`, component: TextField, label: intl.formatMessage(messages.email), errorDisplay: ErrorDisplay.Touched, fullWidth: true }) }), props.onUpdate != null ? (_jsxs(Grid, { item: true, container: true, justifyContent: "space-between", children: [_jsx(Button, { disabled: isRemoving ||
                                            formikProps.isSubmitting ||
                                            !formikProps.dirty ||
                                            !formikProps.isValid, onClick: () => {
                                            formikProps.submitForm();
                                        }, variant: "contained", children: _isNewEntry
                                            ? intl.formatMessage(messages.save)
                                            : intl.formatMessage(messages.update) }), props.onDelete != null ? (_jsx(Button, { onClick: handleOnRemove, variant: "contained", disabled: formikProps.isSubmitting || isRemoving, children: intl.formatMessage(messages.delete) })) : null] })) : null] }) })] })) }));
};
const PropertyOwnersInternal = memo((props) => {
    const intl = useIntl();
    const classes = useStyles();
    const listRef = useRef(null);
    const addButtonRef = useRef(null);
    const { owners } = props;
    let onDelete = props.onDelete;
    useEffect(() => {
        if (owners.length === 0 && props.onAdd)
            props.onAdd(true);
    }, [owners.length, props]);
    if (owners.length === 1 && owners.some(data => data._isNewEntry)) {
        onDelete = undefined;
    }
    const handleDelete = useCallback(async (owner) => {
        const index = owners.findIndex(o => getCustomerId(o) === getCustomerId(owner));
        await onDelete?.(owner);
        requestAnimationFrame(() => {
            const items = listRef.current?.querySelectorAll(`.${classes.ownerDetails}`);
            const target = items?.[index] ?? items?.[index - 1];
            if (target) {
                target.focus();
            }
            else {
                addButtonRef.current?.focus();
            }
        });
    }, [owners, onDelete, props, classes.ownerDetails]);
    return (_jsxs(Grid, { container: true, className: props.className, children: [_jsx(Grid, { container: true, spacing: 1, ref: listRef, children: owners.map((data, index) => (_jsx(Grid, { item: true, xs: 12, children: _jsx(HouseOwnerFromApplicationCustomerElement, { data: data, autoFocus: props.autoFocusId != null &&
                            getCustomerId(data) === props.autoFocusId, onUpdate: props.onUpdate, onDelete: onDelete ? handleDelete : undefined, position: index + 1, total: owners.length }) }, getCustomerId(data)))) }), props.onAdd != null ? (_jsx(Grid, { item: true, xs: 12, children: _jsxs(Button, { ref: addButtonRef, onClick: () => props.onAdd?.(), variant: "outlined", className: classes.addButton, fullWidth: true, children: [_jsx(AddCircleOutlineIcon, { className: classes.addButtonPlusIcon }), intl.formatMessage(messages.addMore)] }) })) : null] }));
});
function getCustomerId(customer) {
    return customer._frontendOnlyId ?? String(customer.customerId);
}
function houseOwnerExistencePredicate(itemFromCollection, needle) {
    return ((needle._frontendOnlyId != null &&
        needle._frontendOnlyId === itemFromCollection._frontendOnlyId) ||
        (needle._frontendOnlyId == null &&
            needle.customerId === itemFromCollection.customerId));
}
function ensureCollectionHasIds(items) {
    return (!isArray(items) ? [] : items || []).map(owner => 
    // Just in case application / calc rules are reloaded but owner are not saved to database. May cause rerenders.
    (owner.customerId == null || owner.customerId === 0) &&
        !owner._frontendOnlyId
        ? { ...owner, _frontendOnlyId: uuid() }
        : owner);
}
export const HouseOwnersFromApplicationCustomersField = memo((props) => {
    const classes = useStyles();
    const customers = useMemo(() => ensureCollectionHasIds(props.field.value), [props.field.value]);
    const allOwners = useMemo(() => customers.filter(o => o.customerRole === CustomerRole.HouseOwner), [customers]);
    const autoFocusIdRef = useRef();
    const addItem = useCallback((skipAutoFocus) => {
        const newId = uuid();
        const newItem = {
            _frontendOnlyId: newId,
            _isNewEntry: true,
            customerRole: CustomerRole.HouseOwner,
            customerId: 0
        };
        let updatedList = [...customers, newItem];
        if (!skipAutoFocus) {
            autoFocusIdRef.current = newId;
        }
        props.form.setFieldValue(props.field.name, updatedList);
    }, [customers, props.field.name, props.form]);
    const deleteItem = useCallback(async (ownerToDelete) => {
        // TODO: Communicate succesful deletion and errors
        let updatedCustomerList = [];
        if (ownerToDelete._isNewEntry) {
            updatedCustomerList = props.field.value.filter(x => x._frontendOnlyId !== ownerToDelete._frontendOnlyId);
        }
        else {
            updatedCustomerList = await doApplicationCustomerRemove({
                customerRole: CustomerRole.HouseOwner,
                customerNumberHint: ownerToDelete.customerNumber
            }).then(res => res?.customers);
        }
        if (updatedCustomerList != null) {
            props.form.setFieldValue(props.field.name, updatedCustomerList);
            return Promise.resolve();
        }
        // Treat unsuccesful delete as error
        return Promise.reject();
    }, [props.field.name, props.field.value, props.form]);
    const updateItem = useCallback(async (updatedData) => {
        let relevantCustomerList = customers;
        const oldCustomerObject = customers.find(o => o.customerRole === CustomerRole.HouseOwner &&
            updatedData.customerId !== 0 &&
            o.customerId === updatedData.customerId);
        const newOrChangedSsn = oldCustomerObject == null ||
            oldCustomerObject.customerNumber !== updatedData.customerNumber;
        const ssnIsAlreadyUsed = newOrChangedSsn &&
            customers.find(o => o.customerRole === CustomerRole.HouseOwner &&
                o.customerNumber === updatedData.customerNumber);
        if (ssnIsAlreadyUsed) {
            return Promise.reject(CustomerMutationErrors.SsnAlreadyUsed);
        }
        if (newOrChangedSsn && updatedData.customerNumber != null) {
            if (oldCustomerObject) {
                // Can discard result since we will be looking up new customer and use customer list from that call
                await doApplicationCustomerRemove({
                    customerRole: CustomerRole.HouseOwner,
                    customerId: oldCustomerObject.customerId
                });
            }
            try {
                const searchResponse = await doApplicationCustomerSearch({
                    customerRole: CustomerRole.HouseOwner,
                    customerType: CustomerType.Private,
                    customerNumber: updatedData.customerNumber
                });
                relevantCustomerList = searchResponse.customers;
                // Make sure we can find our new customer
                updatedData.customerId = searchResponse.foundCustomer.customerId;
                updatedData._frontendOnlyId = undefined;
                updatedData._isNewEntry = false;
            }
            catch (e) {
                if (exceptionIsCustomerMutationError(e)) {
                    return Promise.reject(e);
                }
                return Promise.reject();
            }
        }
        let updatedList = relevantCustomerList.map(customer => {
            // Update all customers with the same customer id
            if (!houseOwnerExistencePredicate(customer, updatedData)) {
                return customer;
            }
            // Safe to assume we will apply our contact data alreay?
            const entry = {
                ...customer,
                phoneMobile: updatedData.phoneMobile,
                email: updatedData.email
            };
            return entry;
        });
        // TODO: do extra validation? Ensure unique ssns?
        props.form.setFieldValue(props.field.name, updatedList);
        // Find resolve value, slightly omtimistic that the customer will exist in the list
        return updatedList.find(x => x.customerRole === CustomerRole.HouseOwner &&
            houseOwnerExistencePredicate(x, updatedData));
    }, [customers, props.field.name, props.form]);
    const maxOwners = 5;
    return (_jsxs(AriaErrorScope, { component: Grid, form: props.form, fieldName: props.field.name, errorDisplay: props.errorDisplay, container: true, spacing: 1, "aria-label": typeof props.label === "string"
            ? props.label
            : props.options?.buttonText, className: clsx(classes.root, props.className), onClick: props.onClick, children: [props.label ? (_jsx(Grid, { item: true, xs: 12, children: _jsx(FormLabel, { component: "legend", children: props.label }) })) : null, _jsx(Grid, { item: true, xs: 12, children: _jsx(PropertyOwnersInternal, { owners: allOwners.filter(o => !o.isMainCustomer), autoFocusId: autoFocusIdRef.current, onAdd: allOwners.length < maxOwners ? addItem : undefined, onUpdate: updateItem, onDelete: deleteItem }) })] }));
});
export const HouseOwnersFromApplicationCustomersSection = (sectionProps) => {
    // This section started as a custom field 😅
    return (_jsx(_Fragment, { children: _jsx(CalcRuleFormikField, { component: HouseOwnersFromApplicationCustomersField, name: "Calculator.CUSTOMERS", className: sectionProps.className, onClick: sectionProps.onClick, literal: sectionProps.literal }) }));
};
addRegisteredSectionLayout({
    displayName: "HouseOwnersFromApplicationCustomersSection",
    name: "HouseOwnersFromApplicationCustomersSection",
    settings: {}
});
HouseOwnersFromApplicationCustomersField.displayName =
    "HouseOwnersFromApplicationCustomersField";
HouseOwnersFromApplicationCustomersSection.displayName =
    "HouseOwnersFromApplicationCustomersSection";
export const HouseOwnersFromApplicationCustomersFields = identityRecordOfFieldEditorOptions({
    HouseOwnersFromApplicationCustomersField: {
        editableOptions: {}
    }
});
//# sourceMappingURL=HouseOwnersFromApplicationCustomersSection.js.map